import React from 'react';
import { SparklesIcon, ZapIcon, HeartPulseIcon, RocketIcon } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
interface PortalSelectionProps {
  onSelectPortal: (portal: 'health' | 'superhuman') => void;
  onNavigate?: (sectionId: string) => void;
}
export function PortalSelection({
  onSelectPortal,
  onNavigate
}: PortalSelectionProps) {
  const handleHealthPortalClick = () => {
    window.scrollTo(0, 0);
    onSelectPortal('health');
  };
  return <div className="min-h-screen flex flex-col" data-id="element-117">
      <Header onNavigate={onNavigate} data-id="element-118" />
      {/* Unified Gradient Background */}
      <div className="flex-1 bg-gradient-to-br from-slate-900 via-fuchsia-950 to-cyan-950 relative overflow-hidden" data-id="element-119">
        {/* Ambient light effects */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-600/20 via-fuchsia-600/20 to-purple-600/20" data-id="element-120"></div>
        <div className="absolute left-0 top-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" data-id="element-121"></div>
        <div className="absolute right-0 top-1/3 w-96 h-96 bg-fuchsia-500/20 rounded-full blur-3xl" data-id="element-122"></div>
        <div className="absolute left-1/2 bottom-1/4 w-96 h-96 bg-purple-500/15 rounded-full blur-3xl" data-id="element-123"></div>
        <div className="max-w-7xl mx-auto px-6 py-16 relative" data-id="element-124">
          {/* Headline Section - sits directly on gradient */}
          <div className="text-center mb-20" data-id="element-125">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-126">
              Precision Cell™{' '}
              <span className="bg-gradient-to-r from-cyan-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent" data-id="element-127">
                Transformation Programs
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-cyan-100 font-medium leading-relaxed" data-id="element-128">
              The new breakthrough method to create the{' '}
              <span className="underline" data-id="element-129">body</span>,{' '}
              <span className="underline" data-id="element-130">brain</span> and{' '}
              <span className="underline" data-id="element-131">life</span> of your dreams.
            </p>
          </div>
          {/* Portal Cards Container */}
          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="element-132">
            {/* Portal Card 1: Health & Vitality */}
            <div onClick={handleHealthPortalClick} className="bg-slate-800/60 backdrop-blur-xl rounded-3xl overflow-hidden shadow-2xl hover:shadow-cyan-500/30 border border-cyan-500/30 hover:border-cyan-400/50 transition-all duration-300 cursor-pointer group relative" data-id="element-133">
              {/* Card gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/20 via-cyan-800/20 to-slate-900/40" data-id="element-134"></div>
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" data-id="element-135"></div>
              {/* Ambient effects inside card */}
              <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-cyan-500/20 rounded-full blur-3xl" data-id="element-136"></div>
              <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-blue-500/15 rounded-full blur-3xl" data-id="element-137"></div>
              <div className="relative p-12 flex flex-col items-center text-center" data-id="element-138">
                {/* Icon */}
                <div className="mb-8 relative" data-id="element-139">
                  <div className="absolute inset-0 bg-cyan-400/40 rounded-full blur-2xl animate-pulse" data-id="element-140"></div>
                  <div className="relative bg-gradient-to-br from-cyan-400 to-blue-500 p-6 rounded-full shadow-2xl" data-id="element-141">
                    <HeartPulseIcon className="w-16 h-16 text-white" data-id="element-142" />
                  </div>
                </div>
                {/* Badge */}
                <div className="inline-flex items-center gap-2 bg-cyan-500/20 backdrop-blur-sm px-6 py-2.5 rounded-full border border-cyan-400/50 mb-8" data-id="element-143">
                  <SparklesIcon className="w-4 h-4 text-cyan-300" data-id="element-144" />
                  <span className="text-sm font-bold text-cyan-200" data-id="element-145">
                    Fix What's Broken
                  </span>
                </div>
                {/* Title */}
                <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-146">
                  Health, Vitality &<br data-id="element-147" />
                  <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent" data-id="element-148">
                    Complete Renewal
                  </span>
                </h2>
                {/* Description */}
                <p className="text-lg text-cyan-100 max-w-xl mb-8 font-medium leading-relaxed" data-id="element-149">
                  Reverse chronic conditions, eliminate symptoms, and reclaim
                  the vibrant health you deserve
                </p>
                {/* Features */}
                <div className="space-y-3 mb-10" data-id="element-150">
                  <div className="flex items-center gap-3 text-cyan-200" data-id="element-151">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full" data-id="element-152"></div>
                    <span className="font-semibold" data-id="element-153">
                      Chronic Disease Reversal
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-cyan-200" data-id="element-154">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full" data-id="element-155"></div>
                    <span className="font-semibold" data-id="element-156">
                      Pain & Symptom Elimination
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-cyan-200" data-id="element-157">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full" data-id="element-158"></div>
                    <span className="font-semibold" data-id="element-159">
                      Energy & Vitality Restoration
                    </span>
                  </div>
                </div>
                {/* CTA Button */}
                <button className="bg-white text-cyan-900 font-bold py-4 px-10 rounded-xl hover:shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 group-hover:scale-105" data-id="element-160">
                  Begin Your Healing Journey
                </button>
              </div>
            </div>
            {/* Portal Card 2: SUPERHUMAN */}
            <div onClick={() => onSelectPortal('superhuman')} className="bg-slate-800/60 backdrop-blur-xl rounded-3xl overflow-hidden shadow-2xl hover:shadow-fuchsia-500/30 border border-fuchsia-500/30 hover:border-fuchsia-400/50 transition-all duration-300 cursor-pointer group relative" data-id="element-161">
              {/* Card gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-600/20 via-purple-800/20 to-slate-900/40" data-id="element-162"></div>
              <div className="absolute inset-0 bg-gradient-to-tl from-transparent via-fuchsia-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" data-id="element-163"></div>
              {/* Ambient effects inside card */}
              <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-fuchsia-500/20 rounded-full blur-3xl" data-id="element-164"></div>
              <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-purple-500/15 rounded-full blur-3xl" data-id="element-165"></div>
              <div className="relative p-12 flex flex-col items-center text-center" data-id="element-166">
                {/* Icon */}
                <div className="mb-8 relative" data-id="element-167">
                  <div className="absolute inset-0 bg-fuchsia-400/40 rounded-full blur-2xl animate-pulse" data-id="element-168"></div>
                  <div className="relative bg-gradient-to-br from-fuchsia-500 to-purple-600 p-6 rounded-full shadow-2xl" data-id="element-169">
                    <RocketIcon className="w-16 h-16 text-white" data-id="element-170" />
                  </div>
                </div>
                {/* Badge */}
                <div className="inline-flex items-center gap-2 bg-fuchsia-500/20 backdrop-blur-sm px-6 py-2.5 rounded-full border border-fuchsia-400/50 mb-8" data-id="element-171">
                  <ZapIcon className="w-4 h-4 text-fuchsia-300" data-id="element-172" />
                  <span className="text-sm font-bold text-fuchsia-200" data-id="element-173">
                    Coming Soon
                  </span>
                </div>
                {/* Title */}
                <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-174">
                  <span className="bg-gradient-to-r from-fuchsia-300 to-purple-300 bg-clip-text text-transparent" data-id="element-175">
                    SUPERHUMAN
                  </span>
                </h2>
                {/* Description */}
                <p className="text-lg text-fuchsia-100 max-w-xl mb-8 font-medium leading-relaxed" data-id="element-176">
                  Transcend human limitations and unlock elite-level
                  performance, cognition, and longevity
                </p>
                {/* Features */}
                <div className="space-y-3 mb-10" data-id="element-177">
                  <div className="flex items-center gap-3 text-fuchsia-200" data-id="element-178">
                    <div className="w-2 h-2 bg-fuchsia-400 rounded-full" data-id="element-179"></div>
                    <span className="font-semibold" data-id="element-180">Cognitive Enhancement</span>
                  </div>
                  <div className="flex items-center gap-3 text-fuchsia-200" data-id="element-181">
                    <div className="w-2 h-2 bg-fuchsia-400 rounded-full" data-id="element-182"></div>
                    <span className="font-semibold" data-id="element-183">
                      Peak Physical Performance
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-fuchsia-200" data-id="element-184">
                    <div className="w-2 h-2 bg-fuchsia-400 rounded-full" data-id="element-185"></div>
                    <span className="font-semibold" data-id="element-186">
                      Biological Age Reversal
                    </span>
                  </div>
                </div>
                {/* CTA Button */}
                <button className="bg-white text-fuchsia-900 font-bold py-4 px-10 rounded-xl hover:shadow-2xl hover:shadow-fuchsia-500/50 transition-all duration-300 group-hover:scale-105" data-id="element-187">
                  Join the Waitlist
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer data-id="element-188" />
    </div>;
}